/**
 * Utilities for splitting and reassembling full HTML documents.
 *
 * The visual editor (contentEditable) can only work with body content —
 * it strips DOCTYPE, <html>, <head>, and <body> tags by design.
 * These helpers let us peel off the structural wrapper before editing
 * and restore it faithfully when saving or sending to Interspire.
 */

export interface HtmlDocumentParts {
  doctype: string;
  headHtml: string;
  bodyAttributes: string;
  bodyContent: string;
  /** True when the source HTML contained at least one structural wrapper tag. */
  hasWrapper: boolean;
}

const DEFAULT_DOCTYPE = '<!DOCTYPE html>';
const DEFAULT_HEAD = '<head>\n<meta charset="utf-8">\n<meta name="viewport" content="width=device-width, initial-scale=1.0">\n</head>';

/**
 * Splits a raw HTML string into structural parts and editable body content.
 * Falls back gracefully when the input is already a bare fragment (no wrapper).
 */
export function splitHtmlDocument(html: string): HtmlDocumentParts {
  if (!html || !html.trim()) {
    return {
      doctype: DEFAULT_DOCTYPE,
      headHtml: DEFAULT_HEAD,
      bodyAttributes: '',
      bodyContent: '',
      hasWrapper: false,
    };
  }

  const trimmed = html.trim();
  const hasDoctype = trimmed.toLowerCase().startsWith('<!doctype');
  const hasHtmlTag = /<html[\s>]/i.test(trimmed);
  const hasBodyTag = /<body[\s>]/i.test(trimmed);
  const hasWrapper = hasDoctype || hasHtmlTag || hasBodyTag;

  if (!hasWrapper) {
    // Bare fragment — nothing to strip
    return {
      doctype: DEFAULT_DOCTYPE,
      headHtml: DEFAULT_HEAD,
      bodyAttributes: '',
      bodyContent: html,
      hasWrapper: false,
    };
  }

  // Use DOMParser for reliable extraction (works in a browser context)
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  const doctypeMatch = trimmed.match(/^(<!DOCTYPE[^>]*>)/i);
  const doctype = doctypeMatch ? doctypeMatch[1] : DEFAULT_DOCTYPE;

  const headEl = doc.head;
  const headHtml = headEl && headEl.innerHTML.trim()
    ? `<head>${headEl.innerHTML}</head>`
    : DEFAULT_HEAD;

  // Preserve any attributes on <body> (e.g. bgcolor, style, class)
  const bodyTagMatch = trimmed.match(/<body([^>]*)>/i);
  const bodyAttributes = bodyTagMatch ? bodyTagMatch[1] : '';

  const bodyContent = doc.body ? doc.body.innerHTML : '';

  return { doctype, headHtml, bodyAttributes, bodyContent, hasWrapper: true };
}

/**
 * Reassembles a full HTML document from structural wrapper parts plus new body content.
 *
 * Do not wrap body innerHTML in extra newlines. Those nodes live *inside* <body>,
 * so split → reassemble on save would keep adding blank lines before and after
 * the real content.
 */
export function reassembleHtml(parts: HtmlDocumentParts, editedBodyContent: string): string {
  const bodyOpen = parts.bodyAttributes ? `<body${parts.bodyAttributes}>` : '<body>';
  const body = editedBodyContent.replace(/^[\r\n]+/, '').replace(/[\r\n]+$/, '');
  return `${parts.doctype}\n<html>\n${parts.headHtml}\n${bodyOpen}${body}</body>\n</html>`;
}

/**
 * contentEditable can resolve href="{{LINK}}" to the current origin
 * (e.g. http://localhost:3000/%7B%7BLINK%7D%7D). Restore the token.
 */
export function restorePlaceholderHrefs(html: string): string {
  return html
    .replace(/href=(["'])[^"']*%7B%7B([A-Z0-9_]+)%7D%7D[^"']*\1/gi, 'href=$1{{$2}}$1')
    .replace(/href=(["'])[^"']*\{\{([A-Z0-9_]+)\}\}[^"']*\1/g, 'href=$1{{$2}}$1');
}

/**
 * Paste handler for contentEditable editors.
 *
 * Browsers wrap each pasted plain-text line in a <p> or <div> block, which adds
 * unwanted extra spacing between lines. This handler intercepts the paste, strips
 * any rich formatting, and inserts plain text with <br> line breaks instead.
 *
 * Compatible with React's SyntheticEvent as well as native ClipboardEvent.
 */
export function handlePlainTextPaste(e: { preventDefault(): void; clipboardData: DataTransfer }): void {
  e.preventDefault();
  const text = e.clipboardData.getData('text/plain');
  if (!text) return;
  const html = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br>');
  // execCommand is deprecated but remains the only cross-browser way to insert
  // at the cursor inside a contentEditable without losing the selection position.
  document.execCommand('insertHTML', false, html);
}
